import { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';

interface CopyPageButtonProps {
  /** CSS selector for the content container to convert to markdown */
  contentSelector: string;
  /** Page title for the markdown header */
  title: string;
}

/**
 * Floating button that converts the visible academy page content to
 * markdown and copies it to the clipboard. Lets users paste the full
 * guide into any AI chat for reference.
 */
export function CopyPageButton({ contentSelector, title }: CopyPageButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const root = document.querySelector(contentSelector);
    if (!root) return;

    const md = htmlToMarkdown(root, title);
    await navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [contentSelector, title]);

  return (
    <button
      onClick={handleCopy}
      className="btn btn-ghost btn-sm gap-1.5 text-base-content/50 hover:text-base-content"
      title="Copy page as Markdown"
    >
      {copied ? (
        <>
          <Check className="w-4 h-4 text-success" />
          <span className="text-xs text-success">Copied!</span>
        </>
      ) : (
        <>
          <Copy className="w-4 h-4" />
          <span className="text-xs">Copy as MD</span>
        </>
      )}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════
   HTML → Markdown converter (DOM-walking, no dependencies)
   ════════════════════════════════════════════════════════════════ */

function htmlToMarkdown(root: Element, pageTitle: string): string {
  const lines: string[] = [`# ${pageTitle}`, ''];
  walk(root, lines, 0);
  // Clean up excessive blank lines
  return lines
    .join('\n')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim() + '\n';
}

function walk(node: Node, lines: string[], depth: number): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    if (text.trim()) {
      lines.push(text.replace(/\s+/g, ' '));
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as HTMLElement;

  // Skip hidden elements, screenshot/video placeholders, and nav sidebars
  if (el.closest('aside') || el.closest('nav')) return;
  const style = window.getComputedStyle(el);
  if (style.display === 'none') return;

  const tag = el.tagName.toLowerCase();

  // Headings
  if (/^h[1-6]$/.test(tag)) {
    const level = parseInt(tag[1]);
    // Offset: page title is h1, so sections start at h2
    const prefix = '#'.repeat(Math.min(level + 1, 6));
    lines.push('', `${prefix} ${textContent(el)}`, '');
    return;
  }

  // Code blocks (pre > code)
  if (tag === 'pre') {
    const codeEl = el.querySelector('code');
    const code = codeEl ? codeEl.textContent || '' : el.textContent || '';
    // Try to detect language from sibling label
    const langLabel = el.querySelector('span')?.textContent?.trim().toLowerCase() || '';
    const lang = langLabel && langLabel.length < 20 ? langLabel : '';
    lines.push('', `\`\`\`${lang}`, code.trim(), '```', '');
    return;
  }

  // Inline code
  if (tag === 'code' && !el.closest('pre')) {
    lines.push(`\`${textContent(el)}\``);
    return;
  }

  // Ordered list items
  if (tag === 'ol') {
    let idx = 1;
    el.childNodes.forEach((child) => {
      if ((child as HTMLElement).tagName?.toLowerCase() === 'li') {
        lines.push(`${idx}. ${textContent(child as HTMLElement).trim()}`);
        idx++;
      }
    });
    lines.push('');
    return;
  }

  // Unordered list items
  if (tag === 'ul') {
    el.childNodes.forEach((child) => {
      if ((child as HTMLElement).tagName?.toLowerCase() === 'li') {
        lines.push(`- ${textContent(child as HTMLElement).trim()}`);
      }
    });
    lines.push('');
    return;
  }

  // Paragraphs and divs with direct text
  if (tag === 'p') {
    lines.push('', textContent(el).trim(), '');
    return;
  }

  // Section elements — add spacing
  if (tag === 'section') {
    lines.push('');
    el.childNodes.forEach((child) => walk(child, lines, depth + 1));
    lines.push('');
    return;
  }

  // Bold / strong
  if (tag === 'strong' || tag === 'b') {
    lines.push(`**${textContent(el)}**`);
    return;
  }

  // Italic / em
  if (tag === 'em' || tag === 'i') {
    lines.push(`*${textContent(el)}*`);
    return;
  }

  // Links
  if (tag === 'a') {
    const href = (el as HTMLAnchorElement).href;
    lines.push(`[${textContent(el)}](${href})`);
    return;
  }

  // Skip buttons, svg, img
  if (['button', 'svg', 'img', 'canvas'].includes(tag)) return;

  // Default: recurse into children
  el.childNodes.forEach((child) => walk(child, lines, depth + 1));
}

/** Extract clean text content from an element */
function textContent(el: Element | HTMLElement): string {
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
}
